import { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import type { EpisodeSetupResponse, LookPreset, LookSettings, MusicMood, SetupSuggestions, StorySetup } from '../../types';
import type { SoundtrackTrack } from '../../player/types';
import { CATEGORY_LABELS } from '../../player/types';
import { COLOR_FILTERS } from '../../player/episodeLook';
import { ActionBar, ChipQuestion, StepHeader, primaryBtn, secondaryBtn } from './ui';

const MUSIC_MOODS: MusicMood[] = ['CELEBRATION', 'EMOTIONAL', 'NOSTALGIC', 'PEACEFUL', 'ADVENTURE'];
type Looks = EpisodeSetupResponse['looks'];

/**
 * Music and finished look, with their own state: used by the wizard's step 6
 * and by the story panel on the episode page. `edits` counts the user's
 * changes, so a caller can save as they happen.
 */
export function useMusicLook(setup: StorySetup, suggested: SetupSuggestions, looks: Looks) {
  const [tracks, setTracks] = useState<SoundtrackTrack[] | null>(null);
  const [musicOn, setMusicOnRaw] = useState(setup.music?.enabled ?? true);
  const [musicMood, setMusicMoodRaw] = useState<MusicMood>(setup.music?.mood ?? suggested.musicMood);
  // Saved songs are the pick; none saved means the mood's songs (also what "Automatic" plays).
  const [trackIds, setTrackIdsRaw] = useState<string[] | null>(setup.music?.trackIds.length ? setup.music.trackIds : null);
  const [showAll, setShowAll] = useState(false);
  const [look, setLookRaw] = useState<LookSettings & { preset: LookPreset }>(
    setup.look ?? { preset: suggested.look, ...looks[suggested.look].settings },
  );
  const [customising, setCustomising] = useState(setup.look?.preset === 'custom');
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const [edits, setEdits] = useState(0);
  const edited = () => setEdits((n) => n + 1);

  const loadTracks = () =>
    api
      .soundtracks()
      .then((lib) => setTracks(lib.tracks))
      .catch(() => setTracks([]));
  useEffect(() => {
    void loadTracks();
  }, []);

  const inMood = (tracks ?? []).filter((t) => t.category === musicMood);
  const shown = showAll ? (tracks ?? []) : inMood;
  // Until the user ticks something, the tracks in the chosen mood are the pick.
  const chosen = trackIds ?? inMood.map((t) => t.id);

  const setMusicOn = (on: boolean) => {
    setMusicOnRaw(on);
    edited();
  };
  const setMusicMood = (m: MusicMood) => {
    setMusicMoodRaw(m);
    setTrackIdsRaw(null);
    edited();
  };
  const toggleTrack = (id: string) => {
    setTrackIdsRaw(chosen.includes(id) ? chosen.filter((t) => t !== id) : [...chosen, id]);
    edited();
  };

  const addMusic = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadNote('Adding…');
    try {
      const res = await api.addSoundtracks(Array.from(files), musicMood);
      const added = res.results.filter((r) => r.track).map((r) => r.track!.id);
      const rejected = res.results.filter((r) => r.status === 'rejected');
      await loadTracks();
      if (added.length > 0) {
        setTrackIdsRaw([...new Set([...chosen, ...added])]);
        edited();
      }
      setUploadNote(
        rejected.length > 0
          ? `${rejected.length} couldn’t be added: ${rejected.map((r) => r.error ?? r.filename).join('; ')}`
          : null,
      );
    } catch (err) {
      setUploadNote((err as Error).message);
    }
  };

  const pickPreset = (preset: Exclude<LookPreset, 'custom'>) => {
    setLookRaw({ preset, ...looks[preset].settings });
    setCustomising(false);
    edited();
  };
  const tweak = (patch: Partial<LookSettings>) => {
    setLookRaw((l) => ({ ...l, ...patch, preset: 'custom' }));
    edited();
  };

  /** The patch for api.saveEpisodeSetup. */
  const toSave = () => ({
    music: { enabled: musicOn, mood: musicMood, trackIds: musicOn ? chosen : [] },
    look,
  });

  return {
    tracks,
    musicOn,
    setMusicOn,
    musicMood,
    setMusicMood,
    inMood,
    shown,
    chosen,
    showAll,
    setShowAll,
    toggleTrack,
    addMusic,
    uploadNote,
    look,
    pickPreset,
    tweak,
    customising,
    setCustomising,
    edits,
    toSave,
  };
}

export type MusicLookState = ReturnType<typeof useMusicLook>;

/** The Music and Finished look sections (state from useMusicLook). */
export function MusicLookFields({
  state,
  suggested,
  looks,
  coverUrl,
}: {
  state: MusicLookState;
  suggested: SetupSuggestions;
  looks: Looks;
  coverUrl: string | null;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const {
    tracks,
    musicOn,
    setMusicOn,
    musicMood,
    setMusicMood,
    inMood,
    shown,
    chosen,
    showAll,
    setShowAll,
    toggleTrack,
    addMusic,
    uploadNote,
    look,
    pickPreset,
    tweak,
    customising,
    setCustomising,
  } = state;

  return (
    <>
      {/* ---- Music ---- */}
      <section className="mb-8 rounded-2xl bg-ink-900 p-5 ring-1 ring-white/5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold">Music</h2>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={musicOn} onChange={(e) => setMusicOn(e.target.checked)} className="h-4 w-4 accent-accent" />
            Play music
          </label>
        </div>
        {musicOn && (
          <>
            <ChipQuestion
              label="Mood"
              hint={`Suggested: ${CATEGORY_LABELS[suggested.musicMood]}`}
              options={MUSIC_MOODS.map((m) => ({ value: m, label: CATEGORY_LABELS[m] }))}
              value={musicMood}
              onChange={(m) => m && setMusicMood(m)}
            />
            {tracks === null ? (
              <p className="text-sm text-white/45">Loading your music…</p>
            ) : tracks.length === 0 ? (
              <p className="text-sm text-white/55">
                No music available yet. Add your own and it’ll be filed under {CATEGORY_LABELS[musicMood]}.
              </p>
            ) : (
              <>
                {shown.length === 0 && (
                  <p className="mb-2 text-sm text-white/55">None of your songs are marked {CATEGORY_LABELS[musicMood]} yet.</p>
                )}
                <ul className="space-y-1">
                  {shown.map((t) => (
                    <li key={t.id}>
                      <label className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-white/5">
                        <input
                          type="checkbox"
                          checked={chosen.includes(t.id)}
                          onChange={() => toggleTrack(t.id)}
                          className="h-4 w-4 accent-accent"
                        />
                        <span className="min-w-0 flex-1 truncate text-sm">{t.title}</span>
                        <span className="text-xs text-white/35">
                          {t.builtIn ? `Built-in · ${CATEGORY_LABELS[t.category]}` : CATEGORY_LABELS[t.category]}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                {inMood.length !== tracks.length && (
                  <button
                    type="button"
                    onClick={() => setShowAll((v) => !v)}
                    className="focus-ring mt-2 rounded text-xs text-white/50 hover:text-white/85"
                  >
                    {showAll ? `Only ${CATEGORY_LABELS[musicMood]}` : `Show all ${tracks.length} songs`}
                  </button>
                )}
              </>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button type="button" onClick={() => fileRef.current?.click()} className={`${secondaryBtn} py-2 text-xs`}>
                Add your own music
              </button>
              {uploadNote && <span className="text-xs text-white/50">{uploadNote}</span>}
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="audio/*"
                className="sr-only"
                tabIndex={-1}
                aria-label="Add music files"
                onChange={(e) => {
                  void addMusic(e.target.files).finally(() => {
                    if (fileRef.current) fileRef.current.value = '';
                  });
                }}
              />
            </div>
          </>
        )}
      </section>

      {/* ---- Look ---- */}
      <section className="rounded-2xl bg-ink-900 p-5 ring-1 ring-white/5">
        <h2 className="mb-1 text-lg font-bold">Finished look</h2>
        <p className="mb-4 text-xs text-white/45">Suggested: {looks[suggested.look].label}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(Object.keys(looks) as Array<keyof Looks>).map((key) => {
            const l = looks[key];
            const on = look.preset === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={on}
                onClick={() => pickPreset(key)}
                className={`focus-ring overflow-hidden rounded-xl text-left ring-2 transition ${
                  on ? 'ring-accent' : 'ring-white/10 hover:ring-white/25'
                }`}
              >
                <div className="relative aspect-video bg-white/5">
                  {coverUrl && (
                    <img
                      src={coverUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      style={{ filter: COLOR_FILTERS[l.settings.colorTreatment] }}
                    />
                  )}
                  {l.settings.vignette && (
                    <span className="pointer-events-none absolute inset-0 shadow-[inset_0_0_40px_rgba(0,0,0,0.75)]" />
                  )}
                </div>
                <div className="p-2.5">
                  <p className="text-sm font-semibold">{l.label}</p>
                  <p className="text-[11px] leading-snug text-white/45">{l.hint}</p>
                </div>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          aria-expanded={customising}
          onClick={() => setCustomising((v) => !v)}
          className="focus-ring mt-4 rounded text-sm font-semibold text-white/70 hover:text-white"
        >
          {customising ? '▾' : '▸'} Customise{look.preset === 'custom' ? ' (your own mix)' : ''}
        </button>
        {customising && (
          <div className="mt-4">
            <ChipQuestion
              label="Pace"
              options={[
                { value: 'relaxed', label: 'Relaxed' },
                { value: 'cinematic', label: 'Cinematic' },
                { value: 'dynamic', label: 'Quick' },
              ]}
              value={look.pace}
              onChange={(v) => v && tweak({ pace: v })}
            />
            <ChipQuestion
              label="Transitions"
              options={[
                { value: 'auto', label: 'Mixed' },
                { value: 'dissolve', label: 'Dissolve' },
                { value: 'fade-black', label: 'Fade to black' },
                { value: 'push', label: 'Push' },
                { value: 'zoom', label: 'Zoom' },
                { value: 'blur', label: 'Blur' },
                { value: 'cut', label: 'Cut' },
              ]}
              value={look.transition}
              onChange={(v) => v && tweak({ transition: v })}
            />
            <ChipQuestion
              label="Colour"
              options={[
                { value: 'natural', label: 'Natural' },
                { value: 'warm', label: 'Warm' },
                { value: 'film', label: 'Film' },
              ]}
              value={look.colorTreatment}
              onChange={(v) => v && tweak({ colorTreatment: v })}
            />
            <ChipQuestion
              label="Photo movement"
              options={[
                { value: 'still', label: 'Still' },
                { value: 'motion', label: 'Slow zoom' },
                { value: 'depth', label: '3D depth' },
              ]}
              value={look.photoAnimation}
              onChange={(v) => v && tweak({ photoAnimation: v })}
            />
            <label className="flex items-center gap-2 text-sm text-white/75">
              <input
                type="checkbox"
                checked={look.vignette}
                onChange={(e) => tweak({ vignette: e.target.checked })}
                className="h-4 w-4 accent-accent"
              />
              Soft dark edges (vignette)
            </label>
          </div>
        )}
      </section>
    </>
  );
}

/**
 * Step 5: the app suggests a music mood and a finished look from the answers;
 * the user can change both, or fine-tune the look. Tracks are the user's own
 * plus the built-in public-domain ones (server/src/services/builtin-music.ts).
 */
export default function StyleStep({
  episodeId,
  setup,
  suggested,
  looks,
  coverUrl,
  onBack,
  onDone,
}: {
  episodeId: string;
  setup: StorySetup;
  suggested: SetupSuggestions;
  looks: Looks;
  coverUrl: string | null;
  onBack: () => void;
  onDone: () => void;
}) {
  const state = useMusicLook(setup, suggested, looks);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finish = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.saveEpisodeSetup(episodeId, { ...state.toSave(), completed: true });
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <StepHeader step={6} title="Music & look" subtitle="Our suggestions are picked from your answers. Change anything you like." />

      <MusicLookFields state={state} suggested={suggested} looks={looks} coverUrl={coverUrl} />

      {error && (
        <p className="mt-4 text-sm text-red-200" role="alert">
          {error}
        </p>
      )}

      <ActionBar>
        <button onClick={onBack} className={secondaryBtn}>
          Back
        </button>
        <button onClick={finish} disabled={saving} className={primaryBtn}>
          {saving ? 'Saving…' : 'Finish'}
        </button>
      </ActionBar>
    </div>
  );
}
